/**
 * Phase F-finale-7 Slice 7b.4d — IA booking-admin API client.
 *
 * Wraps the real-auth seating *booking* surface (eduviaos-backend
 * `app/domains/seating/routes.py`), guarded by `_BOOKING_WRITE_GUARD`
 * (`manage_bookings` OR `book_seat`):
 *
 *   GET  /api/bookings                    (list, student/seat/zone/date/status filters + paging)
 *   POST /api/bookings/{id}/check-in      (mark a held booking checked-in)
 *   POST /api/bookings/{id}/cancel        (cancel a booking — requires a reason)
 *
 * A "booking" is the dynamic-mode *per-session* hold of a seat (or zone in
 * zone-only mode) for a student on a given `date_key`/`slot_key` — distinct
 * from the fixed-mode permanent `SeatAssignment` (Slice 7b.4c). Booking
 * *creation* is a student-facing action (`book_seat`) and is intentionally
 * not exposed here; this admin surface only lists and resolves (check-in /
 * cancel) existing bookings. Tenant + actor are resolved BE-side from the JWT
 * `AuthContext`; none are client-trusted.
 *
 * The wire shapes mirror `app/domains/seating/schemas.py`
 * (`BookingResponse`, `CheckInBookingRequest`, `CancelBookingRequest`,
 * `BookingListResponse`) exactly — any field-shape change on the BE must
 * land here too (Iron Rule #1/#11).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/** A booking's lifecycle status (BE `BookingStatus`). */
export type BookingStatus = "held" | "checked_in" | "completed" | "cancelled" | "no_show";

/** Mirror of `BookingResponse`. */
export interface Booking {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  student_id: string;
  seat_id: string | null;
  zone_id: string | null;
  date_key: string;
  slot_key: string;
  status: string;
  held_at: string;
  checked_in_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  note: string | null;
  created_by: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface BookingListResponse {
  items: Booking[];
  total: number;
}

/** Mirror of `CheckInBookingRequest` — the note is optional (0..2000). */
export interface CheckInBookingInput {
  note?: string | null;
}

/** Mirror of `CancelBookingRequest` — the reason is mandatory (1..2000). */
export interface CancelBookingInput {
  reason: string;
}

export interface BookingListFilters {
  student_id?: string;
  seat_id?: string;
  zone_id?: string;
  date_key?: string;
  status?: BookingStatus;
  branch_id?: string;
  limit?: number;
  offset?: number;
}

// ── React Query hooks ─────────────────────────────────────────────────

export const bookingKeys = {
  all: ["ia", "seating", "bookings"] as const,
  list: (filters?: BookingListFilters) => [...bookingKeys.all, "list", filters ?? {}] as const,
};

function buildBookingsQuery(filters?: BookingListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.student_id) search.set("student_id", filters.student_id);
  if (filters.seat_id) search.set("seat_id", filters.seat_id);
  if (filters.zone_id) search.set("zone_id", filters.zone_id);
  if (filters.date_key) search.set("date_key", filters.date_key);
  if (filters.status) search.set("status", filters.status);
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useBookings(filters?: BookingListFilters, options?: { enabled?: boolean }) {
  const qs = buildBookingsQuery(filters);
  return useQuery<BookingListResponse>({
    queryKey: bookingKeys.list(filters),
    queryFn: () => api.get<BookingListResponse>(`/api/bookings${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
    enabled: options?.enabled ?? true,
  });
}

export interface CheckInBookingVars {
  id: string;
  body?: CheckInBookingInput;
}

export function useCheckInBooking() {
  const qc = useQueryClient();
  return useMutation<Booking, ApiError, CheckInBookingVars>({
    mutationKey: ["ia", "seating", "bookings", "check-in"],
    mutationFn: ({ id, body }) => api.post<Booking>(`/api/bookings/${id}/check-in`, body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: bookingKeys.all });
    },
  });
}

export interface CancelBookingVars {
  id: string;
  body: CancelBookingInput;
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation<Booking, ApiError, CancelBookingVars>({
    mutationKey: ["ia", "seating", "bookings", "cancel"],
    mutationFn: ({ id, body }) => api.post<Booking>(`/api/bookings/${id}/cancel`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: bookingKeys.all });
    },
  });
}
